import React from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea } from 'recharts';
import { downloadGlucoseDataCSV } from '@/lib/csvDownload';

const DetailedGlucoseChart: React.FC<{
  samples: number[][];
  startGlucose: number;
  baselineSamples?: number[][];
}> = ({ samples, startGlucose, baselineSamples }) => {
  if (!samples || samples.length === 0) {
    return <div className="text-center text-muted-foreground">No glucose data available</div>;
  }

  const chartData = samples.map((sample, index) => ({
    time: sample[0] / 60,
    glucose: sample[1],
    baseline: baselineSamples && baselineSamples[index] ? baselineSamples[index][1] : null,
  }));

  const yAxisMin = -30;
  const yAxisMax = 90;
  const yAxisTicks = [-30, 0, 30, 60, 90];

  const chartConfig = {
    glucose: { label: 'Experiment Meal', color: 'hsl(var(--primary))' },
  } as const;

  const timeTicks = [-30, 0, 30, 60, 90, 120, 180];
  const tickLabels = ['-30m', '0m', '30m', '1h', '1h 30m', '2h', '3h'];

  return (
    <>
      <ChartContainer config={chartConfig} className="w-full h-64">
        <LineChart data={chartData}>
          <XAxis
            dataKey="time"
            type="number"
            scale="linear"
            domain={[-30, 150]}
            ticks={timeTicks}
            tickFormatter={(value) => tickLabels[timeTicks.indexOf(value)]}
          />
          <YAxis domain={[yAxisMin, yAxisMax]} ticks={yAxisTicks} tickFormatter={(value) => `${value}`} />
          <ReferenceArea
            y1={-15}
            y2={45}
            fill="hsl(var(--chart-green))"
            fillOpacity={0.12}
            stroke="hsl(var(--chart-green))"
            strokeOpacity={0.25}
          />
          <ReferenceArea
            x1={0}
            x2={30}
            fill="hsl(var(--primary))"
            fillOpacity={0.1}
            stroke="hsl(var(--primary))"
            strokeOpacity={0.3}
            strokeDasharray="2 2"
          />
          <ReferenceLine
            x={0}
            stroke="hsl(var(--primary))"
            strokeDasharray="3 3"
            label={{ value: 'Meal', position: 'insideTopLeft' }}
          />
          <ReferenceLine
            y={0}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="3 3"
            label={{ value: 'Start level', position: 'insideTopRight' }}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          {baselineSamples && (
            <Line type="monotone" dataKey="baseline" stroke="#9ca3af" strokeWidth={1} dot={false} name="Sugar baseline test" />
          )}
          <Line type="monotone" dataKey="glucose" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Experiment Meal" />
        </LineChart>
      </ChartContainer>
      <div className="mt-2 flex items-center justify-center gap-6 text-xs">
        {baselineSamples && (
          <div className="flex items-center gap-2">
            <span className="inline-block h-1.5 w-5 rounded-full" style={{ backgroundColor: '#9ca3af' }} />
            <span className="text-muted-foreground">Sugar baseline test</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="inline-block h-1.5 w-5 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }} />
          <span className="text-muted-foreground">(Your) Experiment Meal</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: 'hsl(var(--chart-green))' }} />
          <span className="text-muted-foreground">Target range (-15 to +45 mg/dL)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: 'hsl(var(--primary))' }} />
          <span className="text-muted-foreground">Minutes after meal</span>
        </div>
      </div>
    </>
  );
};

const MiniGlucoseChart: React.FC<{
  samples: number[][];
  startGlucose: number;
  baselineSamples?: number[][];
  showDownload?: boolean;
}> = ({ samples, startGlucose, baselineSamples, showDownload = true }) => {
  const handleDownloadCSV = () => {
    downloadGlucoseDataCSV(samples, baselineSamples);
  };

  if (!samples || samples.length === 0) {
    return (
      <div className="w-20 h-8 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500">
        No data
      </div>
    );
  }

  const normalizeSamples = (samplesIn: number[][]) => {
    const startingSampleOffset = Math.min(...samplesIn.map((sample) => Math.abs(sample[0])));
    const startingGlucose = samplesIn.find((sample) => Math.abs(sample[0]) === startingSampleOffset)![1];
    return samplesIn.map((sample) => [sample[0], sample[1] - startingGlucose]);
  };

  const normalizedGlucose = normalizeSamples(samples);
  const normalizedBaseline = baselineSamples && normalizeSamples(baselineSamples);
  const glucoseValues = normalizedGlucose.map((sample) => sample[1]);
  const baselineValues = normalizedBaseline ? normalizedBaseline.map((sample) => sample[1]) : [];
  const allValues = [...glucoseValues, ...(baselineValues || [])];
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);
  const range = maxValue - minValue || 1;

  const width = 80;
  const height = 32;
  const padding = 2;

  const points = glucoseValues.map((value, index) => {
    const x = padding + (index / (glucoseValues.length - 1)) * (width - 2 * padding);
    const y = padding + ((maxValue - value) / range) * (height - 2 * padding);
    return `${x},${y}`;
  });
  const pathData = `M ${points.join(' L ')}`;

  const baselinePathData = baselineValues && baselineValues.length > 0
    ? (() => {
        const baselinePoints = baselineValues.map((value, index) => {
          const x = padding + (index / (baselineValues.length - 1)) * (width - 2 * padding);
          const y = padding + ((maxValue - value) / range) * (height - 2 * padding);
          return `${x},${y}`;
        });
        return `M ${baselinePoints.join(' L ')}`;
      })()
    : null;

  return (
    <div className="flex items-center gap-2">
      <HoverCard>
        <HoverCardTrigger asChild>
          <div className="w-20 h-8 cursor-pointer hover:opacity-80 transition-opacity">
            <svg width={width} height={height} className="border rounded">
              {baselinePathData && <path d={baselinePathData} stroke="#9ca3af" strokeWidth="1" fill="none" />}
              <path d={pathData} stroke="#3b82f6" strokeWidth="1" fill="none" />
            </svg>
          </div>
        </HoverCardTrigger>
        <HoverCardContent className="w-auto p-4">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Glucose spike (mg/dL)</h4>
            <DetailedGlucoseChart
              samples={normalizedGlucose}
              startGlucose={startGlucose}
              baselineSamples={normalizedBaseline}
            />
          </div>
        </HoverCardContent>
      </HoverCard>
      {showDownload && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleDownloadCSV}
          title="Download glucose data as CSV"
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default MiniGlucoseChart;
